import { useMutation } from "@tanstack/react-query";
import { useI18n } from "i18n";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { Errors, trackError } from "modules/monitoring";
import { sendPutRequest } from "services/apiService";
import { LoginOptionsConfiguration } from "@remo-co/types";
import actions from "modules/companySettings/redux/actions";
import { useAppDispatch } from "store/hooks";
import { selectMyCompany } from "modules/company/redux/selectors";
import { useSelector } from "react-redux";

const useUpdateLoginOptions = (companyId?: string) => {
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const { t } = useI18n(["settings", "common"]);
  const dispatch = useAppDispatch();
  const company = useSelector(selectMyCompany);

  const { mutate } = useMutation({
    mutationFn: async (
      updatedLoginOptions: Partial<LoginOptionsConfiguration>,
    ) => {
      const response = await sendPutRequest<
        typeof updatedLoginOptions,
        LoginOptionsConfiguration | { status: false; message: string }
      >(
        `/company/${company?.id}/settings/login-options`,
        updatedLoginOptions,
        "PATCH",
      );

      if ("status" in response) {
        throw new Error(response.message);
      }

      return response;
    },

    onSuccess: (loginOptions) => {
      dispatch(actions.setLoginOptions(loginOptions));

      addSuccessNotification({
        message: t("common:saved.success"),
      });
    },

    onError: (error: Error) => {
      trackError(error, {
        label: Errors.UPDATE_LOGIN_OPTIONS,
        companyId,
      });
      addErrorNotification({
        message: t(error.message),
        position: "tc",
      });
    },
  });

  return { updateLoginOptions: mutate };
};

export default useUpdateLoginOptions;
